import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  HospitalForm,
  DEFAULT_HOSPITAL_VALUES,
  useHospitalFormSchemaOptionalContact,
} from '@lp/features/admin/forms/HospitalForm';
import { NO_PRIMARY_CONTACT_VALUE, PrimaryContactForm } from '@lp/features/admin/forms/PrimaryContactForm';
import { AuthContext } from '@lp/core/auth';
import { Hospital } from '@/api/apis/facilities';
import { useUpdateHospitalMutation } from '@/queries/hospital';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { convertToIanaTimezone, getChangedValues, getShortTzByIanaName } from '@/utils/helpers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { useWrappedRemoveHospitalMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { NewHospitalForm } from '@/types/facility.types';

export const HospitalEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<Hospital>();
  const { mutateAsync } = useUpdateHospitalMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);
  const { schema, isLoading: isGeoDataLoading } = useHospitalFormSchemaOptionalContact();

  const hasCreatePermissions = checkPermission(AdminPermissions.HospitalCreate);

  const defaultValues = useMemo<NewHospitalForm>(() => {
    const contactList = data.contacts || [];
    const mainContact = contactList.find((c) => c.isPrimary);

    return {
      ...DEFAULT_HOSPITAL_VALUES,
      name: data.name,
      code: data.code || '',
      logoPath: data.logoPath || '',
      timezone: getShortTzByIanaName(data.location.timezone) || '',
      timezoneIANA: data.location.timezone,
      addressLine: data.location.addressLine,
      country: data.location.country,
      city: data.location.city,
      state: data.location.state,
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      postalCode: data.location.postalCode,
      primaryContactId: mainContact?.id,
      firstName: mainContact?.firstName || '',
      lastName: mainContact?.lastName || '',
      email: mainContact?.email,
      photoPath: mainContact?.photoPath || '',
      primaryPhone: mainContact?.primaryPhone || '',
      secondaryPhone: mainContact?.secondaryPhone || '',
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/hospitals/locations');
  };

  const handleSubmit = async (values: NewHospitalForm) => {
    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const normalizedPrimaryContactId =
        values.primaryContactId === NO_PRIMARY_CONTACT_VALUE ? null : values.primaryContactId;

      const updateBody = {
        name: values.name,
        code: values.code ? values.code : '',
        timezone: values.timezoneIANA || convertToIanaTimezone(values.timezone),
        logoPath: uploadedFile ? uploadedFile[0].fileName : values.logoPath,
        addressLine: values.addressLine,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        longitude: values.longitude,
        latitude: values.latitude,
        primaryContactId: normalizedPrimaryContactId,
      };

      const changedValues = getChangedValues<Partial<NewHospitalForm>>(updateBody, defaultValues);

      await mutateAsync({
        id: data.id,
        ...changedValues,
      });

      toast.success(`Donor Hospital updated successfully.`);
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveHospitalMutation,
    successMessage: 'Donor Hospital deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleHospitalDelete = () => {
    onModalOpen(data.id);
  };

  if (isGeoDataLoading) return null;

  return (
    <>
      <Form<NewHospitalForm> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit Donor Hospital" backToListLink="/admin/hospitals/locations">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleHospitalDelete}
              hasEditPermissions={hasCreatePermissions}
              hasDeletePermissions={hasCreatePermissions}
            />
          </AdminPageHeader>
          <HospitalForm isReadOnly={!hasCreatePermissions} />
          <PrimaryContactForm
            isPhoneRequired={false}
            isRequired={false}
            list={data.contacts}
            isReadOnly
            isDisabled={!hasCreatePermissions}
            isClearableForm
          />
        </div>
      </Form>
      <DeleteModal
        title="Delete Donor Hospital?"
        description="Are you sure you want to delete the Donor Hospital?"
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
